// src/features/tpt_static_data/new.tsx

import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { AutocompleteAsyncField } from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

// Zod schema for the new form
const FormSchema = z.object({
  // Autocomplete for Strategy
  strategy_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_id: z.coerce.number().optional().nullable(),

  // Autocomplete for Time Series File Date
  time_serie_file_date_filename: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          filename: z.string(),
        }),
      ])
      .optional(),
  ),
  time_serie_file_date_id: z.coerce.number().optional().nullable(),
});

export function TptStaticDataEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "strategy_name") {
        const strategyName = fetchedData?.strategy_name || value;
        const strategyId = fetchedData?.strategy_id;

        if (strategyName && strategyId) {
          return {
            text: strategyName,
            id: strategyId,
            name: strategyName,
          };
        }
        return undefined;
      }

      if (key === "time_serie_file_date_filename") {
        const filename = fetchedData?.time_serie_file_date_filename || value;
        const fileId = fetchedData?.time_serie_file_date_id;

        if (filename && fileId) {
          return {
            text: filename,
            id: fileId,
            filename: filename,
          };
        }
        return undefined;
      }

      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                TptStaticData {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="strategy_name"
                    label={t(`${table_name}.columns.strategy_name`, {
                      ns: "tables",
                      defaultValue: "Strategy",
                    })}
                    base_url="etl-quantilia/strategies"
                    column_name="name"
                    id_column="strategy_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="time_serie_file_date_filename"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Time Series File Date",
                    })}
                    base_url="instit-quantilia/time_serie_file_dates"
                    column_name="filename"
                    id_column="time_serie_file_date_id"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["strategy_id", "time_serie_file_date_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
